import React, { ReactNode } from 'react';
import cx from 'classnames';

interface Props {
  children: ReactNode | ReactNode[];
}

const FormContext = React.createContext({});

function Form({ children }: Props) {
  return (
    <FormContext.Provider value={{}}>
      <form className="mb-4">{children}</form>
    </FormContext.Provider>
  );
}

interface NetlifyProps extends Props {
  reference: React.RefObject<HTMLFormElement>;
  name: string;
  action: string;
}

Form.Netlify = function FormNetlify({ reference, name, action, children }: NetlifyProps) {
  return (
    <form
      ref={reference}
      name={name}
      action={action}
      data-netlify="true"
      data-netlify-honeypot="honeypot"
      className="mb-4"
    >
      <input type="hidden" name="form-name" value={name} />
      <input type="text" className="hidden" name="honeypot" />
      <input type="hidden" name="url" value={action} />
      {children}
    </form>
  );
};

Form.Group = function FormGroup({ children }: Props) {
  return <div className="mb-6">{children}</div>;
};

interface LabelProps {
  for: string;
  children: string;
}

Form.Label = function FormLabel({ for: htmlFor, children }: LabelProps) {
  return (
    <label
      className="block text-gray-700 dark:text-white font-bold mb-2"
      id={htmlFor}
      htmlFor={htmlFor}
    >
      {children}
    </label>
  );
};

interface InputProps {
  id: string;
  type?: string;
  placeholder?: string;
  onChangeText: (text: string) => void;
  value: string;
  disabled?: boolean;
  className?: string;
  rows?: number;
}

Form.Input = function FormInput({
  id,
  type,
  placeholder,
  onChangeText,
  value,
  disabled = false,
  className,
}: InputProps) {
  return (
    <input
      key={id}
      id={id}
      name={id}
      type={type || 'input'}
      className={cx(
        'shadow appearance-none border dark:border-gray-700 rounded w-full py-2 px-3 text-gray-700 dark:text-white dark:bg-gray-800 leading-tight focus:outline-none focus:shadow-outline',
        className,
      )}
      placeholder={placeholder || ''}
      value={value}
      disabled={disabled}
      onChange={e => onChangeText(e.target.value)}
    />
  );
};

Form.TextArea = function FormTextArea({ id, placeholder, onChangeText, value, rows }: InputProps) {
  return (
    <textarea
      key={id}
      name={id}
      id={id}
      rows={rows || 10}
      value={value}
      placeholder={placeholder || ''}
      onChange={e => onChangeText(e.target.value)}
      className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 dark:border-gray-700 dark:text-white dark:bg-gray-800 leading-tight focus:outline-none focus:shadow-outline"
    />
  );
};

interface SelectProps {
  id: string;
  value: string;
  disabled?: boolean;
  onChangeOption: (option: string) => void;
  children: React.ReactNode | React.ReactNode[];
}

Form.Select = function FormSelect({
  id,
  value,
  disabled = false,
  onChangeOption,
  children,
}: SelectProps) {
  return (
    <div className="relative flex">
      <select
        className={cx(
          'block h-8 appearance-none w-full bg-white border border-gray-400 dark:border-gray-700 hover:border-gray-500 dark:text-white dark:bg-gray-800 px-4 pr-8 rounded shadow leading-tight focus:outline-none focus:shadow-outline',
          {
            'opacity-50 cursor-not-allowed': disabled,
          },
        )}
        name={id}
        id={id}
        value={value}
        disabled={disabled}
        onChange={e => {
          onChangeOption(e.target.value);
        }}
      >
        {children}
      </select>
      <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-2 text-gray-700">
        <svg
          className="fill-current h-4 w-4"
          xmlns="http://www.w3.org/2000/svg"
          viewBox="0 0 20 20"
        >
          <path d="M9.293 12.95l.707.707L15.657 8l-1.414-1.414L10 10.828 5.757 6.586 4.343 8z" />
        </svg>
      </div>
    </div>
  );
};

interface SelectOptionProp {
  value: string;
  label: string;
  disabled?: boolean;
}

Form.Option = function FormSelectOption({ value, label, disabled }: SelectOptionProp) {
  return (
    <option value={value} disabled={disabled}>
      {label}
    </option>
  );
};

interface MessageProps {
  visible: boolean;
  message: string;
}

Form.Hint = function FormHint({ message, visible }: MessageProps) {
  if (!visible) {
    return null;
  }

  return <p className="mt-2 text-gray-700 text-sm leading-tight">{message}</p>;
};

Form.Error = function FormError({ message, visible }: MessageProps) {
  if (!visible) {
    return null;
  }

  return <p className="mt-2 text-red-700 text-sm leading-tight">{message}</p>;
};

Form.Success = function FormSuccess({ message, visible }: MessageProps) {
  if (!visible) {
    return null;
  }

  return <p className="mt-2 text-green-500 text-xs italic leading-tight">{message}</p>;
};

export default Form;
